import React, { useState, useEffect } from "react";
import { useContent } from "../context/ContentContext";
import { getUsers, addUser, removeUser, checkLogin, setCurrentUser, getCurrentUser, canManageUsers, setUserRole, ROLES } from "../utils/users";
import ImageUrlWithUpload from "../components/admin/ImageUrlWithUpload";

const AUTH_KEY = "himmelstor_admin";

function getIsAuth() {
  return sessionStorage.getItem(AUTH_KEY) === "1";
}

function setAuth(value) {
  if (value) sessionStorage.setItem(AUTH_KEY, "1");
  else sessionStorage.removeItem(AUTH_KEY);
}

export default function Admin() {
  const { content, setSection, resetSection, saveContentToServer } = useContent();
  const [isAuth, setIsAuth] = useState(getIsAuth);
  const [savingSection, setSavingSection] = useState(null);
  const [tab, setTab] = useState("home");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [users, setUsers] = useState(getUsers);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("editor");
  const [userMessage, setUserMessage] = useState({ type: "", text: "" });
  const [visitorStats, setVisitorStats] = useState({ total: 0, unique: 0 });

  useEffect(() => {
    setIsAuth(getIsAuth());
  }, []);

  useEffect(() => {
    if (tab === "users" && !canManageUsers()) setTab("home");
  }, [tab]);

  useEffect(() => {
    const readStats = () => {
      try {
        const total = Number(localStorage.getItem("himmelstor_total_visits") || "0");
        const unique = Number(localStorage.getItem("himmelstor_unique_visitors") || "0");
        setVisitorStats({ total, unique });
      } catch (e) {
        setVisitorStats({ total: 0, unique: 0 });
      }
    };
    readStats();
    window.addEventListener("storage", readStats);
    return () => window.removeEventListener("storage", readStats);
  }, []);

  useEffect(() => {
    if (!isAuth) return undefined;
    const INACTIVITY_LIMIT_MS = 60 * 1000;
    let timer = null;

    const forceLogout = () => {
      setAuth(false);
      setCurrentUser(null);
      setIsAuth(false);
      alert("Session timed out after 1 minute of inactivity. Please log in again.");
    };

    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(forceLogout, INACTIVITY_LIMIT_MS);
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [isAuth]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError("");
    const trimmedUser = (username || "").trim().toLowerCase();
    if (checkLogin(username, password)) {
      setAuth(true);
      setCurrentUser(trimmedUser);
      setIsAuth(true);
    } else {
      setLoginError("Invalid username or password.");
    }
  };

  const handleLogout = () => {
    setAuth(false);
    setCurrentUser(null);
    setIsAuth(false);
  };

  const handleSectionSave = async (sectionKey) => {
    if (!window.confirm("Are you sure you want to save the changes to this section?")) return;
    setSavingSection(sectionKey);
    try {
      await saveContentToServer();
      alert("Success — your changes were saved to the server.");
    } catch (err) {
      alert(err.message || "Save failed.");
    } finally {
      setSavingSection(null);
    }
  };

  const allTabs = ["home", "about", "products", "services", "agroTourism", "gallery", "sustainability", "blog", "contact", "enquiries", "footer", "navbar", "users"];
  const tabs = canManageUsers() ? allTabs : allTabs.filter((t) => t !== "users");
  const currentUser = getCurrentUser();
  const metrics = [
    { title: "Products", value: String((content.products?.items || []).length), status: "Catalog" },
    { title: "Gallery Images", value: String((content.gallery?.images || []).length), status: "Media" },
    { title: "Blog Posts", value: String((content.blog?.posts || []).length), status: "Content" },
  ];
  const trafficStats = [
    { title: "Total Visits", value: String(visitorStats.total), note: "Local tracker" },
    { title: "Unique Visitors", value: String(visitorStats.unique), note: "Per browser" },
    { title: "Avg. Time on Site", value: "03:48", note: "Demo metric" },
  ];
  const trafficPoints = [34, 42, 30, 58, 49, 63, 72];
  const trafficPath = trafficPoints
    .map((value, idx) => {
      const x = (idx / (trafficPoints.length - 1)) * 100;
      const y = 100 - value;
      return `${idx === 0 ? "M" : "L"} ${x},${y}`;
    })
    .join(" ");

  if (!isAuth) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="card shadow">
              <div className="card-body p-4">
                <h2 className="text-success mb-4">Admin Login</h2>
                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input type="text" className="form-control" value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="username" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                  </div>
                  {loginError && <div className="alert alert-danger py-2">{loginError}</div>}
                  <button type="submit" className="btn btn-success w-100">Log In</button>
                </form>
                <p className="text-muted small mt-3 mb-0">Default login: admin / admin123</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-0" style={{ color: "#1b3022" }}>Command Center</h1>
          {currentUser && <span className="text-muted small">Logged in as {currentUser.username} ({ROLES[currentUser.role] || currentUser.role})</span>}
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={handleLogout}>Log Out</button>
      </div>
      <div className="row g-3 mb-4">
        {metrics.map((m) => (
          <div key={m.title} className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <p className="text-uppercase text-muted small mb-1">{m.title}</p>
                <div className="d-flex justify-content-between align-items-end">
                  <h3 className="mb-0" style={{ color: "#1b3022" }}>{m.value}</h3>
                  <span className="badge" style={{ background: "rgba(197,160,89,0.2)", color: "#8a6a2c" }}>{m.status}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0" style={{ color: "#1b3022" }}>Site Traffic</h5>
            <span className="small text-muted">Daily Visitors</span>
          </div>
          <div className="row g-3 mb-3">
            {trafficStats.map((s) => (
              <div key={s.title} className="col-md-4">
                <div className="rounded-3 border p-3 h-100 bg-white">
                  <p className="text-muted text-uppercase small mb-1">{s.title}</p>
                  <h4 className="mb-1" style={{ color: "#1b3022" }}>{s.value}</h4>
                  <p className="small mb-0" style={{ color: "#8a6a2c" }}>{s.note}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-3 border p-3 bg-white">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: 180 }}>
              <polyline
                points="0,100 100,100"
                fill="none"
                stroke="rgba(27,48,34,0.12)"
                strokeWidth="0.6"
              />
              <path
                d={trafficPath}
                fill="none"
                stroke="#1b3022"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={trafficPath}
                fill="none"
                stroke="#c5a059"
                strokeWidth="0.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
      <ul className="nav nav-tabs flex-wrap mb-4">
        {tabs.map((t) => (
          <li key={t} className="nav-item">
            <button type="button" className={`nav-link ${tab === t ? "active" : ""}`} onClick={() => { setTab(t); if (t === "users") setUsers(getUsers()); }}>
              {t === "agroTourism" ? "Agro-Tourism" : t === "users" ? "Users" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          </li>
        ))}
      </ul>

      {tab === "home" && (
        <div className="card card-body">
          <h5>Home</h5>

          <h6 className="text-success mt-3">Slideshow (home page)</h6>
          <p className="text-muted small">Add image URL or upload from your device. Order: top = first slide.</p>
          {(content.home.slideshow || []).map((slide, idx) => (
            <div key={idx} className="border rounded p-3 mb-3 bg-light">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Slide {idx + 1}</strong>
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => {
                  const slideshow = (content.home.slideshow || []).filter((_, i) => i !== idx);
                  setSection("home", { slideshow });
                }}>Remove</button>
              </div>
              <ImageUrlWithUpload
                label="Image URL (or upload)"
                value={slide.src || ""}
                onChange={(url) => {
                  const slideshow = [...(content.home.slideshow || [])];
                  slideshow[idx] = { ...slideshow[idx], src: url };
                  setSection("home", { slideshow });
                }}
                className="mb-2"
              />
              <div className="mb-0">
                <label className="form-label small">Caption (optional)</label>
                <input className="form-control form-control-sm" placeholder="Slide caption" value={slide.caption || ""} onChange={(e) => {
                  const slideshow = [...(content.home.slideshow || [])];
                  slideshow[idx] = { ...slideshow[idx], caption: e.target.value };
                  setSection("home", { slideshow });
                }} />
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-outline-success btn-sm mb-3" onClick={() => setSection("home", { slideshow: [...(content.home.slideshow || []), { src: "", caption: "" }] })}>+ Add slide</button>

          <hr />
          <div className="mb-3">
            <label className="form-label">Hero title</label>
            <input className="form-control" value={content.home.heroTitle} onChange={(e) => setSection("home", { heroTitle: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Hero text</label>
            <textarea className="form-control" rows={3} value={content.home.heroSubtext} onChange={(e) => setSection("home", { heroSubtext: e.target.value })} />
          </div>
          <div className="mb-3">
            <ImageUrlWithUpload
              label="Hero image URL (or upload)"
              value={content.home.heroImage || ""}
              onChange={(url) => setSection("home", { heroImage: url })}
            />
          </div>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Card 1 title</label>
              <input className="form-control" value={content.home.card1Title} onChange={(e) => setSection("home", { card1Title: e.target.value })} />
              <label className="form-label mt-1">Card 1 text</label>
              <input className="form-control" value={content.home.card1Text} onChange={(e) => setSection("home", { card1Text: e.target.value })} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Card 2 title</label>
              <input className="form-control" value={content.home.card2Title} onChange={(e) => setSection("home", { card2Title: e.target.value })} />
              <label className="form-label mt-1">Card 2 text</label>
              <input className="form-control" value={content.home.card2Text} onChange={(e) => setSection("home", { card2Text: e.target.value })} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Card 3 title</label>
              <input className="form-control" value={content.home.card3Title} onChange={(e) => setSection("home", { card3Title: e.target.value })} />
              <label className="form-label mt-1">Card 3 text</label>
              <input className="form-control" value={content.home.card3Text} onChange={(e) => setSection("home", { card3Text: e.target.value })} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("home")}>
              {savingSection === "home" ? "Saving..." : "Save Home"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("home")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "about" && (
        <div className="card card-body">
          <h5>About</h5>
          {["title", "intro", "vision", "mission", "philosophy", "founder", "certifications"].map((key) => (
            <div key={key} className="mb-3">
              <label className="form-label">{key}</label>
              {key === "intro" || key === "vision" || key === "mission" || key === "philosophy" || key === "founder" || key === "certifications" ? (
                <textarea className="form-control" rows={2} value={content.about[key] || ""} onChange={(e) => setSection("about", { [key]: e.target.value })} />
              ) : (
                <input className="form-control" value={content.about[key] || ""} onChange={(e) => setSection("about", { [key]: e.target.value })} />
              )}
            </div>
          ))}
          <div className="mb-3">
            <label className="form-label">Core values (one per line)</label>
            <textarea className="form-control" rows={4} value={(content.about.values || []).join("\n")} onChange={(e) => setSection("about", { values: e.target.value.split("\n").filter(Boolean) })} />
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("about")}>
              {savingSection === "about" ? "Saving..." : "Save About"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("about")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="card card-body">
          <h5>Products</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.products.title} onChange={(e) => setSection("products", { title: e.target.value })} />
          </div>
          {(content.products.items || []).map((item, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <h6>Product {idx + 1}</h6>
              {["name", "description", "availability", "methods"].map((field) => (
                <div key={field} className="mb-2">
                  <label className="form-label small">{field}</label>
                  <input className="form-control form-control-sm" value={item[field] || ""} onChange={(e) => {
                    const items = [...(content.products.items || [])];
                    items[idx] = { ...items[idx], [field]: e.target.value };
                    setSection("products", { items });
                  }} />
                </div>
              ))}
              <ImageUrlWithUpload
                label="image"
                value={item.image || ""}
                onChange={(url) => {
                  const items = [...(content.products.items || [])];
                  items[idx] = { ...items[idx], image: url };
                  setSection("products", { items });
                }}
                className="mb-2"
              />
            </div>
          ))}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("products")}>
              {savingSection === "products" ? "Saving..." : "Save Products"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("products")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "services" && (
        <div className="card card-body">
          <h5>Services</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.services.title} onChange={(e) => setSection("services", { title: e.target.value })} />
          </div>
          {(content.services.items || []).map((item, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <h6>Service {idx + 1}</h6>
              {["name", "description", "icon"].map((field) => (
                <div key={field} className="mb-2">
                  <label className="form-label small">{field}</label>
                  <input className="form-control form-control-sm" value={item[field] || ""} onChange={(e) => {
                    const items = [...(content.services.items || [])];
                    items[idx] = { ...items[idx], [field]: e.target.value };
                    setSection("services", { items });
                  }} />
                </div>
              ))}
            </div>
          ))}
          <div className="mb-3">
            <label className="form-label">Advisory areas (comma-separated)</label>
            <input className="form-control" value={(content.services.advisoryAreas || []).join(", ")} onChange={(e) => setSection("services", { advisoryAreas: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Target audience (comma-separated)</label>
            <input className="form-control" value={(content.services.targetAudience || []).join(", ")} onChange={(e) => setSection("services", { targetAudience: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("services")}>
              {savingSection === "services" ? "Saving..." : "Save Services"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("services")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "agroTourism" && (
        <div className="card card-body">
          <h5>Agro-Tourism</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.agroTourism.title} onChange={(e) => setSection("agroTourism", { title: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Book box title</label>
            <input className="form-control" value={content.agroTourism.bookTitle} onChange={(e) => setSection("agroTourism", { bookTitle: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Book box text</label>
            <textarea className="form-control" rows={2} value={content.agroTourism.bookText} onChange={(e) => setSection("agroTourism", { bookText: e.target.value })} />
          </div>
          {(content.agroTourism.activities || []).map((act, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <h6>Activity {idx + 1}</h6>
              {["name", "description"].map((field) => (
                <div key={field} className="mb-2">
                  <label className="form-label small">{field}</label>
                  <input className="form-control form-control-sm" value={act[field] || ""} onChange={(e) => {
                    const activities = [...(content.agroTourism.activities || [])];
                    activities[idx] = { ...activities[idx], [field]: e.target.value };
                    setSection("agroTourism", { activities });
                  }} />
                </div>
              ))}
              <ImageUrlWithUpload
                label="image"
                value={act.image || ""}
                onChange={(url) => {
                  const activities = [...(content.agroTourism.activities || [])];
                  activities[idx] = { ...activities[idx], image: url };
                  setSection("agroTourism", { activities });
                }}
                className="mb-2"
              />
            </div>
          ))}
          <h6 className="mt-3">Gallery images (Agro-Tourism page)</h6>
          {(content.agroTourism.galleryImages || []).map((img, idx) => (
            <div key={idx} className="mb-3 border rounded p-2 bg-light">
              <ImageUrlWithUpload
                label="Image URL (or upload)"
                value={img.src || ""}
                onChange={(url) => {
                  const galleryImages = [...(content.agroTourism.galleryImages || [])];
                  galleryImages[idx] = { ...galleryImages[idx], src: url };
                  setSection("agroTourism", { galleryImages });
                }}
                className="mb-2"
              />
              <input className="form-control form-control-sm" placeholder="Alt text" value={img.alt || ""} onChange={(e) => {
                const galleryImages = [...(content.agroTourism.galleryImages || [])];
                galleryImages[idx] = { ...galleryImages[idx], alt: e.target.value };
                setSection("agroTourism", { galleryImages });
              }} />
            </div>
          ))}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("agroTourism")}>
              {savingSection === "agroTourism" ? "Saving..." : "Save Agro-Tourism"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("agroTourism")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "gallery" && (
        <div className="card card-body">
          <h5>Gallery</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.gallery.title} onChange={(e) => setSection("gallery", { title: e.target.value })} />
          </div>
          {(content.gallery.images || []).map((img, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <ImageUrlWithUpload
                label="Image URL (or upload)"
                value={img.src || ""}
                onChange={(url) => {
                  const images = [...(content.gallery.images || [])];
                  images[idx] = { ...images[idx], src: url };
                  setSection("gallery", { images });
                }}
                className="mb-2"
              />
              <label className="form-label small">Caption</label>
              <input className="form-control" value={img.caption || ""} onChange={(e) => {
                const images = [...(content.gallery.images || [])];
                images[idx] = { ...images[idx], caption: e.target.value };
                setSection("gallery", { images });
              }} />
            </div>
          ))}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("gallery")}>
              {savingSection === "gallery" ? "Saving..." : "Save Gallery"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("gallery")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "sustainability" && (
        <div className="card card-body">
          <h5>Sustainability</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.sustainability.title} onChange={(e) => setSection("sustainability", { title: e.target.value })} />
          </div>
          {(content.sustainability.items || []).map((item, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <label className="form-label small">Title</label>
              <input className="form-control mb-2" value={item.title || ""} onChange={(e) => {
                const items = [...(content.sustainability.items || [])];
                items[idx] = { ...items[idx], title: e.target.value };
                setSection("sustainability", { items });
              }} />
              <label className="form-label small">Text</label>
              <input className="form-control" value={item.text || ""} onChange={(e) => {
                const items = [...(content.sustainability.items || [])];
                items[idx] = { ...items[idx], text: e.target.value };
                setSection("sustainability", { items });
              }} />
            </div>
          ))}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("sustainability")}>
              {savingSection === "sustainability" ? "Saving..." : "Save Sustainability"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("sustainability")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "blog" && (
        <div className="card card-body">
          <h5>Blog</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.blog.title} onChange={(e) => setSection("blog", { title: e.target.value })} />
          </div>
          {(content.blog.posts || []).map((post, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <h6>Post {idx + 1}</h6>
              {["title", "excerpt", "date", "link"].map((field) => (
                <div key={field} className="mb-2">
                  <label className="form-label small">{field}</label>
                  <input className="form-control form-control-sm" value={post[field] || ""} onChange={(e) => {
                    const posts = [...(content.blog.posts || [])];
                    posts[idx] = { ...posts[idx], [field]: e.target.value };
                    setSection("blog", { posts });
                  }} />
                </div>
              ))}
            </div>
          ))}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("blog")}>
              {savingSection === "blog" ? "Saving..." : "Save Blog"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("blog")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "contact" && (
        <div className="card card-body">
          <h5>Contact</h5>
          {["title", "phone", "email", "businessHours"].map((key) => (
            <div key={key} className="mb-3">
              <label className="form-label">{key}</label>
              <input className="form-control" value={content.contact[key] || ""} onChange={(e) => setSection("contact", { [key]: e.target.value })} />
            </div>
          ))}
          <div className="mb-3">
            <label className="form-label">Map embed URL</label>
            <input className="form-control" value={content.contact.mapEmbed || ""} onChange={(e) => setSection("contact", { mapEmbed: e.target.value })} />
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("contact")}>
              {savingSection === "contact" ? "Saving..." : "Save Contact"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("contact")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "enquiries" && (
        <div className="card card-body">
          <h5>Enquiries</h5>
          <div className="mb-3">
            <label className="form-label">Section title</label>
            <input className="form-control" value={content.enquiries.title} onChange={(e) => setSection("enquiries", { title: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Response note</label>
            <textarea className="form-control" rows={2} value={content.enquiries.responseNote} onChange={(e) => setSection("enquiries", { responseNote: e.target.value })} />
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("enquiries")}>
              {savingSection === "enquiries" ? "Saving..." : "Save Enquiries"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("enquiries")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "footer" && (
        <div className="card card-body">
          <h5>Footer</h5>
          {["title", "tagline", "phone", "email", "companyReg"].map((key) => (
            <div key={key} className="mb-3">
              <label className="form-label">{key}</label>
              {key === "tagline" ? (
                <textarea className="form-control" rows={2} value={content.footer[key] || ""} onChange={(e) => setSection("footer", { [key]: e.target.value })} />
              ) : (
                <input className="form-control" value={content.footer[key] || ""} onChange={(e) => setSection("footer", { [key]: e.target.value })} />
              )}
            </div>
          ))}
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("footer")}>
              {savingSection === "footer" ? "Saving..." : "Save Footer"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("footer")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "navbar" && (
        <div className="card card-body">
          <h5>Navbar brand</h5>
          <div className="mb-3">
            <label className="form-label">Brand name</label>
            <input className="form-control" value={content.navbar.brandName} onChange={(e) => setSection("navbar", { brandName: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Brand highlight (e.g. Farm)</label>
            <input className="form-control" value={content.navbar.brandHighlight} onChange={(e) => setSection("navbar", { brandHighlight: e.target.value })} />
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3 pt-3 border-top">
            <button type="button" className="btn btn-success" disabled={savingSection !== null} onClick={() => handleSectionSave("navbar")}>
              {savingSection === "navbar" ? "Saving..." : "Save Navbar"}
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => resetSection("navbar")}>Reset to default</button>
          </div>
        </div>
      )}

      {tab === "users" && canManageUsers() && (
        <div className="card card-body">
          <h5>Admin Users & Rights</h5>
          <p className="text-muted small">Add users and set rights. <strong>Admin</strong> can edit all content and manage users. <strong>Editor</strong> can only edit site content (no Users tab).</p>
          {userMessage.text && (
            <div className={`alert py-2 ${userMessage.type === "error" ? "alert-danger" : "alert-success"}`}>
              {userMessage.text}
            </div>
          )}
          <div className="row mb-4">
            <div className="col-md-6">
              <h6 className="text-success">Add user</h6>
              <div className="mb-2">
                <label className="form-label small">Username</label>
                <input type="text" className="form-control" value={newUsername} onChange={(e) => { setNewUsername(e.target.value); setUserMessage({ type: "", text: "" }); }} placeholder="Username" />
              </div>
              <div className="mb-2">
                <label className="form-label small">Password</label>
                <input type="password" className="form-control" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setUserMessage({ type: "", text: "" }); }} placeholder="Password" />
              </div>
              <div className="mb-2">
                <label className="form-label small">Rights</label>
                <select className="form-select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                  <option value="editor">{ROLES.editor}</option>
                  <option value="admin">{ROLES.admin}</option>
                </select>
              </div>
              <button type="button" className="btn btn-success" onClick={() => {
                setUserMessage({ type: "", text: "" });
                const result = addUser(newUsername, newPassword, newRole);
                if (result.ok) {
                  setUserMessage({ type: "success", text: "User added. They can now log in with " + (newRole === "admin" ? "full" : "editor") + " rights." });
                  setNewUsername("");
                  setNewPassword("");
                  setNewRole("editor");
                  setUsers(getUsers());
                } else {
                  setUserMessage({ type: "error", text: result.error });
                }
              }}>Add user</button>
            </div>
          </div>
          <h6 className="text-success">Current users & rights</h6>
          <ul className="list-group">
            {users.map((u) => (
              <li key={u.username} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span>{u.username}</span>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0 small text-muted">Rights:</label>
                  <select className="form-select form-select-sm w-auto" value={u.role || "editor"} onChange={(e) => {
                    setUserMessage({ type: "", text: "" });
                    const result = setUserRole(u.username, e.target.value);
                    if (result.ok) {
                      setUserMessage({ type: "success", text: "Rights updated." });
                      setUsers(getUsers());
                    } else {
                      setUserMessage({ type: "error", text: result.error });
                    }
                  }}>
                    <option value="editor">{ROLES.editor}</option>
                    <option value="admin">{ROLES.admin}</option>
                  </select>
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => {
                    setUserMessage({ type: "", text: "" });
                    const result = removeUser(u.username);
                    if (result.ok) {
                      setUserMessage({ type: "success", text: "User removed." });
                      setUsers(getUsers());
                    } else {
                      setUserMessage({ type: "error", text: result.error });
                    }
                  }} disabled={users.length <= 1}>Remove</button>
                </div>
              </li>
            ))}
          </ul>
          {users.length <= 1 && <p className="text-muted small mt-2 mb-0">At least one user is required. You cannot remove the last user.</p>}
        </div>
      )}
    </div>
  );
}
